import {
    Add,
    Box,
    Button,
    PremiumFeature,
    TextBlock,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import { t } from "i18next";
import React from "react";

import style from "./home-sub-menu.scss";
import { ConversationManagement } from "components/ai-panel/ai-menu/AIMenu";
import { useAIPanelContext } from "components/ai-panel/AIPanelContext";
import { LockIcon } from "components/icons/LockIcon";
import { useIsRouteEnabled } from "components/navigation/hooks/useIsRouteEnabled";
import { RouteDefinition } from "components/router/Routes";
import { AUTH_AI_ASSISTANT } from "domain/authority";
import { useIsAiHomeEnabled } from "services/feature/useIsAiHomeEnabled";
import { hasAiAssistantLicenseBundle } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";

import testIds from "testIds.json";

interface Props {
    mainRoute: RouteDefinition;
}

export const HomeSubmenu = (props: Props) => {
    const isRouteEnabled = useIsRouteEnabled();
    const { sessions, activeSessionId, conversationManagementHandlers } = useAIPanelContext();

    const isAiHomeEnabled = useIsAiHomeEnabled();
    const hasNoAiLicense = !hasAiAssistantLicenseBundle();
    const isUserAuthorizedForAiAssistant = userSessionService.userHasAnyAuthority([AUTH_AI_ASSISTANT]);

    if (!props.mainRoute || !isRouteEnabled(props.mainRoute)) {
        return null;
    }

    return (
        <>
            {isAiHomeEnabled && conversationManagementHandlers ? (
                <Box paddingTop={"Xxs"} columnLayout className={style.container}>
                    <ConversationManagement
                        sessions={sessions}
                        disabled={conversationManagementHandlers.disabled}
                        activeSessionId={activeSessionId}
                        onNewConversation={conversationManagementHandlers.onNewConversation}
                        onLoadConversation={conversationManagementHandlers.onLoadConversation}
                        onSessionDeleted={conversationManagementHandlers.onSessionDeleted}
                        onSessionRenamed={conversationManagementHandlers.onSessionRenamed}
                        subNavigation
                    />
                </Box>
            ) : (
                <>
                    {(hasNoAiLicense || !isUserAuthorizedForAiAssistant) && (
                        <Tooltip placement="right">
                            <TooltipContent>
                                {hasNoAiLicense ? (
                                    <Box gap="Xxxs" columnLayout>
                                        <TextBlock disableBottomSpacing>
                                            <strong>{t("AiAgent.advertisedNewConversation.title")}</strong>
                                        </TextBlock>
                                        <TextBlock disableBottomSpacing>
                                            {t("AiAgent.advertisedNewConversation.description")}
                                        </TextBlock>
                                        <TextBlock disableBottomSpacing>
                                            {t("AiAgent.advertisedNewConversation.contactBlancco")}
                                        </TextBlock>
                                    </Box>
                                ) : (
                                    <Box gap="Xxxs" columnLayout>
                                        <TextBlock disableBottomSpacing>
                                            <strong>{t("AiAgent.accessNeeded.title")}</strong>
                                        </TextBlock>
                                        <TextBlock disableBottomSpacing>
                                            {t("AiAgent.accessNeeded.description")}
                                        </TextBlock>
                                    </Box>
                                )}
                            </TooltipContent>
                            <TooltipTrigger>
                                <Box
                                    justifyContent="SpaceBetween"
                                    verticalAlignment="Center"
                                    className={style.advertisedNewConversationRow}
                                >
                                    <Button
                                        variant="Tertiary"
                                        icon={<Add color={"--app-subNavigationPanel-iconColor"} />}
                                        disabled
                                        testId={testIds.navigation.home.advertisedNewConversationButton}
                                    >
                                        {t("AiAgent.newConversation")}
                                    </Button>
                                    {hasNoAiLicense ? (
                                        <PremiumFeature size={20} color={"--app-subNavigationPanel-iconColor"} />
                                    ) : (
                                        <LockIcon size={20} color={"var(--app-subNavigationPanel-iconColor)"} />
                                    )}
                                </Box>
                            </TooltipTrigger>
                        </Tooltip>
                    )}
                </>
            )}
        </>
    );
};
